/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import PageTransition from '../PageTransition';
import {
  LayoutDashboard,
  Calendar,
  Users,
  FileText,
  Send,
  Palette,
  Megaphone,
  UserCog,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Inbox,
  Share2,
  Headphones,
  CheckSquare,
  DollarSign,
  MessageCircle,
  BarChart3,
  User,
  Mail,
  MessageSquareQuote,
  Activity,
  Sparkles,
  Lock,
  Gift,
  Layout,
  Rss,
  ShieldAlert,
  Bot,
  FolderOpen,
} from 'lucide-react';

import Img from '../Img';
const sidebarLinks = [
  { name: 'Dashboard', path: '/admin', icon: LayoutDashboard, module: 'dashboard', roles: ['super_admin', 'membership_manager', 'marketing_team', 'events_manager', 'committee_manager', 'membership_lead', 'membership_support', 'blog_reviewer'], section: 'main' },
  { name: 'AI Agent', path: '/admin/ai-agent', icon: Bot, requires: 'ai_agent_access', roles: ['super_admin', 'membership_manager', 'marketing_team', 'events_manager', 'committee_manager', 'membership_lead', 'membership_support', 'blog_reviewer', 'member'], section: 'main' },
  { name: 'Homepage Manager', path: '/admin/homepage', icon: Layout, roles: ['super_admin', 'marketing_team'], section: 'main' },
  { name: 'Newsletter Center', path: '/admin/newsletter-center', icon: Mail, roles: ['super_admin', 'events_manager', 'membership_lead', 'membership_support', 'marketing_team', 'blog_reviewer'], section: 'main' },
  { name: 'External News', path: '/admin/external-news', icon: Rss, roles: ['super_admin', 'marketing_team'], section: 'main' },
  { name: 'Email Communications', path: '/admin/email-communications', icon: Mail, module: 'email_communications', roles: ['super_admin', 'marketing_team', 'membership_lead'], section: 'main' },
  { name: 'Testimonials', path: '/admin/testimonials', icon: MessageSquareQuote, roles: ['super_admin', 'marketing_team', 'membership_lead'], section: 'main' },
  { name: 'Events', path: '/admin/events', icon: Calendar, module: 'events', roles: ['super_admin', 'events_manager'], section: 'main' },
  { name: 'Members', path: '/admin/members', icon: Users, module: 'members', roles: ['super_admin', 'membership_manager', 'membership_lead', 'membership_support'], section: 'main' },
  { name: 'Member Center', path: '/admin/member-center', icon: UserCog, module: 'member_center', roles: ['super_admin', 'membership_manager', 'membership_lead', 'membership_support'], section: 'main' },
  { name: 'Activation Cleanup', path: '/admin/activation-audit', icon: ShieldAlert, module: 'members', roles: ['super_admin', 'membership_lead', 'membership_support'], section: 'main' },
  { name: 'Applications', path: '/admin/applications', icon: UserCog, module: 'applications', roles: ['super_admin', 'membership_lead', 'membership_support'], section: 'main' },
  { name: 'Membership Plans', path: '/admin/membership-plans', icon: DollarSign, module: 'membership_plans', roles: ['super_admin', 'membership_lead', 'membership_support'], section: 'main' },
  { name: 'Member Deals Access', path: '/admin/member-deals-access', icon: Lock, module: 'membership_plans', roles: ['super_admin', 'membership_lead'], section: 'main' },
  { name: 'Member Deals', path: '/admin/member-deals', icon: Gift, module: 'membership_plans', roles: ['super_admin', 'membership_lead'], section: 'main' },
  { name: 'Blog & News', path: '/admin/blog', icon: FileText, module: 'blog_news', roles: ['super_admin', 'marketing_team', 'blog_reviewer'], section: 'main' },
  { name: 'Media Library', path: '/admin/media', icon: FolderOpen, roles: ['super_admin', 'marketing_team', 'events_manager', 'blog_reviewer', 'membership_lead', 'membership_support'], section: 'main' },
  { name: 'News Ads', path: '/admin/news-ads', icon: Sparkles, roles: ['super_admin'], section: 'main' },
  { name: 'Marketing Requests', path: '/admin/marketing-requests', icon: Send, module: 'marketing_requests', roles: ['super_admin', 'marketing_team'], section: 'main' },
  { name: 'Brand Requests', path: '/admin/brand-requests', icon: Palette, module: 'brand_requests', roles: ['super_admin', 'marketing_team'], section: 'main' },
  { name: 'Announcements', path: '/admin/announcements', icon: Megaphone, module: 'announcements', roles: ['super_admin', 'membership_manager', 'membership_lead', 'membership_support'], section: 'main' },
  { name: 'Newsletter', path: '/admin/newsletter', icon: Mail, module: 'newsletter_center', roles: ['super_admin', 'marketing_team', 'membership_lead', 'membership_support'], section: 'main' },
  { name: 'Welcome Experience', path: '/admin/welcome-experience', icon: Sparkles, module: 'platform_settings', roles: ['super_admin', 'marketing_team'], section: 'main' },
  { name: 'Invitations', path: '/admin/invitations', icon: Send, module: 'newsletter_center', roles: ['super_admin', 'membership_lead', 'membership_support'], section: 'main' },
  // Review Center
  { name: 'Blog Review', path: '/admin/blog-review', icon: CheckSquare, module: 'blog_review', roles: ['super_admin', 'blog_reviewer', 'marketing_team'], section: 'review' },
  { name: 'Social Requests', path: '/admin/social-review', icon: Share2, module: 'social_requests', roles: ['super_admin', 'marketing_team'], section: 'review' },
  { name: 'Committee Apps', path: '/admin/committee-apps', icon: Users, module: 'committees', roles: ['super_admin', 'committee_manager'], section: 'review' },
  { name: 'Community', path: '/admin/community', icon: MessageCircle, module: 'community_moderation', roles: ['super_admin', 'marketing_team', 'membership_lead'], section: 'review' },
  { name: 'Support Inbox', path: '/admin/support', icon: Headphones, module: 'support_inbox', roles: ['super_admin', 'membership_lead', 'membership_support'], section: 'review' },
  // Settings
  { name: 'User Presence', path: '/admin/presence', icon: Activity, module: 'user_management', roles: ['super_admin', 'membership_lead', 'membership_support'], section: 'settings' },
  { name: 'Board Dashboard', path: '/admin/board', icon: BarChart3, module: 'user_management', roles: ['super_admin', 'membership_lead'], section: 'settings' },
  { name: 'Leadership', path: '/admin/leadership', icon: Users, module: 'user_management', roles: ['super_admin'], section: 'settings' },
  { name: 'User Management', path: '/admin/users', icon: UserCog, module: 'user_management', roles: ['super_admin'], section: 'settings' },
  { name: 'AI Agent Activity', path: '/admin/ai-agent/activity', icon: Bot, roles: ['super_admin'], section: 'settings' },
];

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Lock body scroll when the mobile sidebar is open so the sidebar's own
  // scroll is the only vertical scroll on screen (iOS Safari fix).
  useEffect(() => {
    if (sidebarOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [sidebarOpen]);

  // Filter by role AND per-user module_permissions (or admin_modules for leadership)
  const isLeadershipAdmin = user?.admin_access && user?.leadership_level && user?.role === 'member';
  
  const filteredLinks = sidebarLinks.filter(link => {
    // AI Agent link: visible only if user has ai_agent_access flag OR is super admin
    if (link.requires === 'ai_agent_access') {
      return user?.role === 'super_admin' || !!user?.ai_agent_access;
    }
    // Super admin always sees everything
    if (user?.role === 'super_admin') return true;
    
    // Leadership users with admin_access: check admin_modules
    if (isLeadershipAdmin) {
      // Never show User Management or Leadership Management to non-super-admins
      if (link.module === 'user_management') return false;
      // Dashboard is always visible for leadership admins
      if (link.module === 'dashboard') return true;
      // Check admin_modules
      const adminMods = user?.admin_modules || {};
      return adminMods[link.module] === true;
    }
    
    // Must have role access first (standard admin roles)
    if (!link.roles.includes(user?.role)) return false;
    // Check per-user module_permissions overrides
    const perms = user?.module_permissions;
    if (perms && typeof perms === 'object' && link.module in perms) {
      return perms[link.module] === true;
    }
    // Default: role grants access
    return true;
  });

  const getRoleName = (role) => {
    if (isLeadershipAdmin) return 'Leadership Admin';
    const roleNames = {
      super_admin: 'Super Admin',
      membership_manager: 'Membership Manager',
      membership_lead: 'Membership Team Lead',
      membership_support: 'Membership Support',
      marketing_team: 'Marketing Team',
      events_manager: 'Events Manager',
      committee_manager: 'Committee Manager',
      blog_reviewer: 'Blog Reviewer',
      member: 'Member'
    };
    return roleNames[role] || role;
  };

  return (
    <div className="admin-shell min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#001028] flex items-center justify-between px-4 z-50 border-b border-white/8">
        <button
          onClick={() => setSidebarOpen(true)}
          className="text-white p-2"
          data-testid="admin-mobile-menu"
        >
          <Menu className="w-6 h-6" />
        </button>
        <Img variant="generic"
          src="/brand/logo-color-alt.png"
          alt="DCCF"
          className="h-8"
        />
        <Link to="/portal" className="text-white/70 hover:text-white p-2" title="Back to Portal" data-testid="admin-to-portal-mobile">
          <User className="w-5 h-5" />
        </Link>
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 u-glass z-50"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 w-64 bg-[#001028] text-white z-50
        flex flex-col
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
      style={{ height: '100dvh', maxHeight: '100dvh', boxShadow: '1px 0 0 rgba(255,255,255,0.04)' }}
      >
        {/* Sidebar Header */}
        <div className="h-[72px] flex-shrink-0 flex items-center justify-between px-5 border-b border-white/8">
          <Link to="/admin" data-testid="admin-logo" className="block">
            <Img variant="generic"
              src="/brand/logo-color-alt.png"
              alt="DCCF"
              className="h-9"
            />
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white/70 hover:text-white p-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="flex-shrink-0 px-4 py-3.5 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#E63946] flex items-center justify-center text-white font-semibold text-[13px] shadow-[0_2px_6px_rgba(230,57,70,0.35)]">
              {user?.name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold truncate text-white/95">{user?.name || 'Admin'}</p>
              <p className="text-[11px] text-white/50 truncate mt-0.5">{getRoleName(user?.role)}</p>
            </div>
          </div>
        </div>

        {/* Navigation — scrolls independently, fills remaining space */}
        <nav
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 pt-3 pb-6 admin-scroll"
          style={{ WebkitOverflowScrolling: 'touch' }}
          data-testid="admin-sidebar-nav"
        >
          {filteredLinks.filter(l => l.section === 'main').map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setSidebarOpen(false)}
                className={`admin-sidebar-link mb-0.5 ${isActive ? 'admin-sidebar-link--active' : ''}`}
                data-testid={`admin-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <link.icon className="w-[17px] h-[17px]" />
                <span>{link.name}</span>
                {isActive && <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-70" />}
              </Link>
            );
          })}

          {/* Review Center Section */}
          {filteredLinks.some(l => l.section === 'review') && (
            <>
              <p className="admin-sidebar-section-label">Review Center</p>
              {filteredLinks.filter(l => l.section === 'review').map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`admin-sidebar-link mb-0.5 ${isActive ? 'admin-sidebar-link--active' : ''}`}
                    data-testid={`admin-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon className="w-[17px] h-[17px]" />
                    <span>{link.name}</span>
                    {isActive && <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-70" />}
                  </Link>
                );
              })}
            </>
          )}

          {/* Settings Section */}
          {filteredLinks.some(l => l.section === 'settings') && (
            <>
              <p className="admin-sidebar-section-label">Settings</p>
              {filteredLinks.filter(l => l.section === 'settings').map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`admin-sidebar-link mb-0.5 ${isActive ? 'admin-sidebar-link--active' : ''}`}
                    data-testid={`admin-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon className="w-[17px] h-[17px]" />
                    <span>{link.name}</span>
                    {isActive && <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-70" />}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* Logout & Back to Site — sticky to bottom of sidebar, always reachable */}
        <div
          className="flex-shrink-0 p-3 border-t border-white/8 space-y-0.5 bg-[#001028]"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <Link
            to="/portal"
            className="admin-sidebar-link"
            data-testid="admin-back-to-portal"
          >
            <ChevronRight className="w-[17px] h-[17px] rotate-180" />
            <span>Member Portal</span>
          </Link>
          <Link
            to="/"
            className="admin-sidebar-link"
            data-testid="admin-back-to-site"
          >
            <ChevronRight className="w-[17px] h-[17px] rotate-180" />
            <span>Back to Website</span>
          </Link>
          <button
            onClick={handleLogout}
            className="admin-sidebar-link w-full text-left"
            style={{ color: '#fda4af' }}
            data-testid="admin-logout"
          >
            <LogOut className="w-[17px] h-[17px]" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <PageTransition>
          <Outlet />
        </PageTransition>
      </main>
    </div>
  );
};

export default AdminLayout;
